import { TenantFeature } from '../../types/tenant.types';

/**
 * Static navigate targets surfaced in the command palette's "Navigate" group.
 * Mirrors the sidebar's top-level sections — kept as a flat array instead of
 * importing from `EnhancedSidebar.tsx` to avoid a circular/heavy dependency
 * (the sidebar pulls in icons, queries, lucide-react, etc.). If the sidebar
 * gets a new top-level item, add it here too.
 */
export interface NavigateTarget {
  section: string;
  label: string;
  path: string;
  /** Hidden when the tenant lacks this feature (mirrors the sidebar's gating). */
  feature?: TenantFeature;
  /** Hidden unless the tenant has at least one of these. */
  anyFeature?: TenantFeature[];
  /** Extra search terms, for pages users look for under another name. */
  keywords?: string[];
}

export const NAVIGATE_TARGETS: NavigateTarget[] = [
  // Revenue
  { section: 'Revenue', label: 'Analytics', path: '/dashboard' },
  { section: 'Revenue', label: 'Renewals', path: '/renewals' },
  { section: 'Revenue', label: 'Finance', path: '/finance' },
  // Workspace
  {
    section: 'Workspace',
    label: 'Inbox',
    path: '/inbox',
    feature: TenantFeature.WORK_INBOX,
    keywords: ['email', 'mail', 'gmail', 'messages', 'work inbox'],
  },
  {
    section: 'Workspace',
    label: 'Inbox settings',
    path: '/inbox/settings',
    feature: TenantFeature.WORK_INBOX,
    keywords: ['digest', 'categories', 'skip inbox', 'learned senders', 'email settings'],
  },
  { section: 'Workspace', label: 'Tasks', path: '/tasks' },
  // Strategy
  {
    section: 'Strategy',
    label: 'OKR Cycle',
    path: '/okr',
    // Once granted, as in the sidebar: /okr offers the switch when OKRs are
    // not turned on yet.
    feature: TenantFeature.OKR,
    // "Objectives" and "goals" are what people call these; "OKR" is jargon
    // half the company will not type.
    keywords: ['objectives', 'key results', 'goals', 'okrs', 'quarter'],
  },
  {
    section: 'Strategy',
    label: 'My OKRs',
    path: '/my-okrs',
    feature: TenantFeature.OKR,
    keywords: ['my key results', 'check in', 'check-in', 'goals', 'okrs', 'what do I own'],
  },
  {
    section: 'Strategy',
    label: 'Hypotheses',
    path: '/okr/hypotheses',
    feature: TenantFeature.OKR,
    keywords: ['bets', 'experiments', 'tests', 'in flight', 'okrs'],
  },
  {
    section: 'Strategy',
    label: 'Hypothesis learnings',
    path: '/okr/hypotheses/learnings',
    feature: TenantFeature.OKR,
    keywords: ['learnings', 'experiments', 'what we learned', 'calibration'],
  },
  {
    section: 'Strategy',
    label: 'Coverage',
    path: '/okr/coverage',
    feature: TenantFeature.OKR,
    keywords: ['alignment', 'company key results', 'teams', 'gaps', 'okrs', 'who is working on'],
  },
  // Customers
  { section: 'Customers', label: 'Clients', path: '/clients' },
  { section: 'Customers', label: 'Subscriptions', path: '/subscriptions' },
  { section: 'Customers', label: 'Outdated pins', path: '/subscriptions/outdated' },
  {
    section: 'Customers',
    label: 'Companies',
    path: '/companies',
    feature: TenantFeature.CRM,
    // Formerly "Prospects" — keep the old name findable.
    keywords: ['prospects', 'accounts', 'organizations'],
  },
  {
    section: 'Customers',
    label: 'Contacts',
    path: '/contacts',
    feature: TenantFeature.CRM,
  },
  {
    section: 'Customers',
    label: 'Pipeline',
    path: '/pipeline',
    feature: TenantFeature.CRM,
    keywords: ['deals', 'leads', 'board', 'crm'],
  },
  {
    section: 'Customers',
    label: 'Pipeline stages',
    path: '/pipeline/stages',
    feature: TenantFeature.CRM,
    keywords: ['stages', 'columns', 'statuses', 'edit pipeline', 'pipeline settings'],
  },
  // Library
  {
    section: 'Library',
    label: 'Documents',
    path: '/library',
    anyFeature: [TenantFeature.CRM, TenantFeature.SUPPORT],
    keywords: [
      'library',
      'documents',
      'proposals',
      'decks',
      'contracts',
      'pricing sheets',
      'attachments',
    ],
  },
  {
    section: 'Library',
    label: 'Forms',
    path: '/forms',
    feature: TenantFeature.FORMS,
    keywords: ['form builder', 'submissions', 'survey', 'intake', 'embed'],
  },
  {
    section: 'Library',
    label: 'Email templates',
    path: '/library/templates/email',
    feature: TenantFeature.CRM,
    keywords: ['templates', 'snippets', 'canned responses', 'saved replies'],
  },
  {
    section: 'Library',
    label: 'Payment reminder emails',
    path: '/library/templates/reminders',
    feature: TenantFeature.FINANCE,
    keywords: ['templates', 'dunning', 'invoice email', 'overdue', 'collections'],
  },
  // Catalog
  { section: 'Catalog', label: 'Markets', path: '/market-config' },
  { section: 'Catalog', label: 'Products', path: '/catalog' },
  { section: 'Catalog', label: 'Discounts', path: '/discounts' },
  {
    section: 'Catalog',
    label: 'Catalog learning',
    path: '/catalog/learning',
    keywords: ['import', 'autonomy', 'earned', 'proposed products', 'catalog gaps'],
  },
  { section: 'Catalog', label: 'Feature Flags', path: '/product-config/feature-flags' },
  // Admin
  { section: 'Admin', label: 'Import', path: '/import' },
  {
    section: 'Admin',
    label: 'Import history',
    path: '/import/history',
    keywords: ['roll back import', 'undo import', 'delete imported invoices'],
  },
  {
    section: 'Admin',
    label: 'Import contacts and companies',
    path: '/import/crm',
    feature: TenantFeature.CRM,
    keywords: ['csv', 'xlsx', 'spreadsheet', 'contacts import', 'companies import'],
  },
  { section: 'Admin', label: 'Users', path: '/users' },
  { section: 'Admin', label: 'Org Settings', path: '/settings' },
  { section: 'Admin', label: 'My Account Settings', path: '/account' },
];
